import { AttachmentTypes } from '~publish/legacy/composer/composer/AppConstants'
import { gbpChannelDataValidation } from '~publish/legacy/composer/composer/lib/validation/utils/gbpChannelDataValidation'
import {
  PostValidationError,
  Services,
  SERVICES_WITHOUT_SHARE_AGAIN_ACTION,
} from '~publish/legacy/constants'
import {
  getDateString,
  isInThePast,
} from '~publish/legacy/duplicate-server/formatters'

import type { ServiceDefinition } from '~publish/legacy/constants/services/types'

import type { PublishingPost } from '../graphqlTypes'
import type { RpcUpdate } from '../types'

type PostCampaign =
  | RpcUpdate['campaignDetails']
  | {
      name: string
      color: { value: string }
    }

export type LegacyPost = {
  id?: string
  isDraft?: boolean
  isSent?: boolean
  isPastReminder?: boolean
  isPastDue?: boolean
  isReminder?: boolean
  isCustomScheduled?: boolean
  isInstagramReminder?: boolean
  type?: string
  status?: string
  postDetails?: RpcUpdate['postDetails'] & {
    errorLabel?: string
  }
  retweet?: RpcUpdate['retweet']
  dueAt?: number | null
  due_at?: number | null
  scheduledAt?: number | null
  scheduled_at?: number | null
  channel_data?: RpcUpdate['channel_data']
  channelData?: RpcUpdate['channelData']
  attachments?: ReadonlyArray<{
    __typename?: string
    text?: string
  }> | null
  needsApproval?: boolean
  media?: RpcUpdate['media'] | ReadonlyArray<{ type?: string }> | null
  source_url?: string | null
  sourceUrl?: string | null
  profileId?: string
  channel?: {
    id: string
    service: string
  }
  channelId?: string
  profile_service?: string
  channelService?: string
  createdBy?: string
  user?: {
    id?: string
    name?: string
  }
  text?: string | null
  error?: unknown
  via?: string | null
  campaign?: PostCampaign | null
  campaignDetails?: PostCampaign | null
  can_send_direct?: boolean
  serviceLink?: string
  updateType?: string
}

const servicesWithoutShareAgainAction: readonly (string | undefined)[] =
  SERVICES_WITHOUT_SHARE_AGAIN_ACTION

const isLegacyPostError = (
  error: unknown,
): error is {
  text?: unknown
  link?: unknown
  link_text?: unknown
} => typeof error === 'object' && error !== null

const hasRepinFlag = (value: object): value is { is_repin?: boolean } =>
  'is_repin' in value

const hasPictureMedia = (value: object): value is { picture?: string } =>
  'picture' in value

const hasVideoMedia = (value: object): value is { video?: { id?: string } } =>
  'video' in value

const getId = (post: LegacyPost): string | undefined => post?.id

const isDraft = (post: LegacyPost): boolean => {
  // RPC post logic
  if (post?.isDraft) return true

  // GraphQL post logic
  if (post?.type === 'draft') return true
  return false
}

const isSent = (post: Pick<LegacyPost, 'isSent' | 'status'>): boolean => {
  // RPC post logic
  if (post?.isSent) return true

  // GraphQL post logic
  if (
    post?.status === 'sent' ||
    post?.status === 'pending_tiktok' ||
    post?.status === 'service'
  )
    return true

  return false
}

const isPastReminder = (post: LegacyPost): boolean => {
  // RPC post logic
  if (post?.isPastReminder) return true

  // GraphQL post logic
  if (post.status === 'notified') return true

  return false
}

const isPendingTiktok = (post: LegacyPost): boolean => {
  // GraphQL post logic
  if (post?.status === 'pending_tiktok') return true

  return false
}

const isRetweet = (post: LegacyPost): boolean => {
  // RPC post logic
  if (post?.postDetails) return post.postDetails.isRetweet

  // GraphQL post logic
  return post.retweet !== undefined
}

const isPastDue = (post: LegacyPost): boolean => {
  // RPC post logic
  if (post?.isPastDue !== undefined) return post.isPastDue

  // GraphQL post logic
  if (post?.dueAt) return isInThePast(post?.dueAt)
  return false
}

const getScheduledAt = (post: LegacyPost): number | null | undefined => {
  return post?.scheduledAt || post?.scheduled_at
}

const getDueAt = (post: LegacyPost): number | null | undefined => {
  return post?.due_at || post?.dueAt
}

// It's common to think that the concept of a post
// being scheduled depends on it having a scheduled at
// date, whereas it actually depends on the due at date.
// This helper aims to help abstract that confusion.
const isScheduled = (post: LegacyPost): boolean => {
  return !!getDueAt(post)
}

const getFirstComment = (post: LegacyPost): string | undefined => {
  // RPC post logic
  if (post?.channel_data?.instagram?.comment_text)
    return post?.channel_data?.instagram?.comment_text
  if (post?.channel_data?.linkedin?.comment_text)
    return post?.channel_data?.linkedin?.comment_text

  // GraphQL post logic
  const graphFirstComment = post?.attachments?.find(
    (attachment) => attachment?.__typename === 'PublishingPostFirstComment',
  )
  if (graphFirstComment) return graphFirstComment?.text
}

const needsApproval = (post: LegacyPost): boolean => {
  if (isDraft(post) && post.needsApproval) return true
  return false
}

const getLocation = (post: LegacyPost): string => {
  // RPC post logic
  if (post?.channel_data?.instagram?.service_geolocation_name) {
    return post?.channel_data?.instagram?.service_geolocation_name
  }

  // GraphQL post logic
  if (post?.channelData?.instagram?.service_geolocation_name) {
    return post?.channelData?.instagram?.service_geolocation_name
  }

  return ''
}

const getThreadsLocation = (post: LegacyPost): string => {
  // RPC post logic
  if (post?.channel_data?.threads?.location_name) {
    return post?.channel_data?.threads?.location_name
  }

  // GraphQL post logic
  if (post?.channelData?.threads?.location_name) {
    return post?.channelData?.threads?.location_name
  }

  return ''
}

const userIsPostOwner = (
  post: LegacyPost | null | undefined,
  userId: string | undefined,
): boolean => {
  if (!post || !userId) return false

  // RPC post logic
  if (post?.user?.id === userId) return true

  // GraphQL post logic
  if (post?.createdBy === userId) return true

  return false
}

const hasText = (post: LegacyPost): boolean => {
  return !!post?.text
}

const hasImage = (post: LegacyPost): boolean => {
  // RPC post logic
  const media = post?.media
  if (media && hasPictureMedia(media) && media.picture) return true

  // GraphQL post logic
  if (Array.isArray(media) && media.some((item) => item?.type === 'image'))
    return true

  return false
}

const hasVideo = (post: LegacyPost): boolean => {
  // RPC post logic
  const media = post?.media
  if (media && hasVideoMedia(media) && media.video?.id) return true

  // GraphQL post logic
  if (Array.isArray(media) && media.some((item) => item.type === 'video'))
    return true

  return false
}

const hasSourceUrl = (post: LegacyPost): boolean => {
  return !!post?.source_url || !!post?.sourceUrl
}

const getChannelId = (post: LegacyPost): string | undefined => {
  // RPC || GraphQL
  return post?.profileId || post?.channel?.id || post?.channelId
}

const getChannelName = (post: LegacyPost): string | undefined => {
  // RPC || GraphQL
  return post?.profile_service || post?.channel?.service || post?.channelService
}

const getChannel = (post: LegacyPost): ServiceDefinition | undefined => {
  const channelName = getChannelName(post)
  if (channelName) return Services.get(channelName)
}

const validatePost = (post: LegacyPost): string | null => {
  const channel = getChannel(post)

  if (
    !hasImage(post) &&
    !hasVideo(post) &&
    channel?.requiredAttachmentType === AttachmentTypes.MEDIA
  ) {
    return PostValidationError.MISSING_IMAGE_OR_VIDEO
  }

  if (!hasSourceUrl(post) && !hasVideo(post) && channel?.requiresSourceUrl) {
    return PostValidationError.MISSING_SOURCE_URL
  }

  if (!hasText(post) && channel?.requiresText) {
    return PostValidationError.MISSING_TEXT
  }

  if (
    channel?.name === 'googlebusiness' &&
    /**
     * TODO: It's a hotfix for GBP missing channelData when created through Zapier.
     * A proper fix is to add this on save for the API endpoint (on the backend)
     */
    post.channelData
  ) {
    const validationErrors = gbpChannelDataValidation.validateGBPChannelData(
      // The legacy post shape leaves updateType optional.
      // @ts-expect-error -- Preserve the existing validator boundary for legacy posts without updateType.
      post.updateType,
      post.channelData?.googlebusiness,
    )
    if (validationErrors.length > 0) {
      return validationErrors[0].message
    }
  }

  return null
}

const isPublishingPost = (
  post?: RpcUpdate | PublishingPost,
): post is PublishingPost => {
  return !!post && '__typename' in post && post?.__typename === 'PublishingPost'
}

const hasCampaign = (post: LegacyPost): boolean => {
  return !!(post?.campaign || post?.campaignDetails)
}

const getCampaign = (post: LegacyPost): PostCampaign | null | undefined => {
  return post?.campaignDetails || post?.campaign
}

const isCustomScheduled = (post: LegacyPost): boolean => {
  // RPC post logic
  if (post?.postDetails) return post.postDetails.isCustomScheduled

  // GraphQL post logic
  return post?.isCustomScheduled || !!post.scheduled_at
}

const isInstagramReminder = (post: LegacyPost): boolean => {
  // RPC post logic
  if (post?.postDetails) return post?.postDetails?.isInstagramReminder

  // GraphQL post logic
  return (
    post?.isInstagramReminder ||
    (post.profile_service === 'instagram' && !post.can_send_direct)
  )
}

const isReminder = (post: LegacyPost): boolean | null | undefined => {
  // RPC post logic
  return post?.isReminder
}

const getFormatedCreatedAt = (
  post: { createdAt: number },
  {
    twentyFourHourTime,
    timezone,
  }: { twentyFourHourTime: boolean; timezone: string },
): string => {
  const createdAt = post?.createdAt
  return getDateString(createdAt, timezone, {
    createdAt,
    twentyFourHourTime,
  })
}

const getError = (post: LegacyPost): unknown => {
  if (post?.postDetails) {
    return post.postDetails.error
  }

  const status = post?.status
  if (status !== 'error') {
    return null
  }

  const error = post?.error
  return isLegacyPostError(error)
    ? typeof error.text === 'string'
      ? error.text
      : ''
    : error || ''
}

const hasError = (post: LegacyPost): boolean => !!getError(post)

const getErrorLink = (post: LegacyPost): string | null | undefined => {
  // RPC post logic
  if (post?.postDetails) return post.postDetails.errorLink

  // GraphQL post logic
  return post.status === 'error' && isLegacyPostError(post.error)
    ? typeof post.error.link === 'string'
      ? post.error.link
      : null
    : null
}

const getErrorLabel = (post: LegacyPost): string => {
  // RPC post logic
  if (post?.postDetails)
    return post.postDetails.errorLabel
      ? post.postDetails.errorLabel
      : 'Learn More'

  // GraphQL post logic
  return post.status === 'error' && isLegacyPostError(post.error)
    ? typeof post.error.link_text === 'string'
      ? post.error.link_text
      : 'Learn More'
    : 'Learn More'
}

const isFetchedPostFromService = (
  post: LegacyPost | null | undefined,
  service: string,
): boolean => {
  if (!service || !post) {
    return false
  }
  return (
    getChannelName(post) === service &&
    // via is campitalized in postParser, but not when coming from gateway.
    post.via?.toLowerCase() === service
  )
}

const isShareAgainActionSupported = (args: {
  channelName: string | undefined
  post: LegacyPost
}): boolean => {
  const { channelName, post } = args
  if (!channelName) return false

  if (
    SERVICES_WITHOUT_SHARE_AGAIN_ACTION.includes(channelName) &&
    PostEntity.isFetchedPostFromService(post, channelName)
  ) {
    return false
  }

  return true
}

const isPostChannelSupportedNewPostAnalytics = (args: {
  post: LegacyPost
}): boolean => {
  const { post } = args
  const channelName = getChannelName(post)

  return servicesWithoutShareAgainAction.includes(channelName)
}

const hasServiceLink = (
  post: RpcUpdate | PublishingPost | null | undefined,
): boolean => {
  return post !== null && post !== undefined && 'serviceLink' in post
    ? !!post.serviceLink
    : false
}

const getPostAuthor = (post: RpcUpdate | PublishingPost): string => {
  let user = ''
  // RPC post logic
  if ('user' in post) user = post?.user?.name || ''
  // GraphQL post logic
  else if (post?.createdBy) user = post?.createdBy || ''
  return user
}

const isRepin = (post: LegacyPost): boolean => {
  // RPC check
  const rpcPinterest = post.channel_data?.pinterest
  if (rpcPinterest && hasRepinFlag(rpcPinterest) && rpcPinterest.is_repin) {
    return rpcPinterest.is_repin
  }

  // Graphql check
  const graphPinterest = post.channelData?.pinterest
  if (
    graphPinterest &&
    hasRepinFlag(graphPinterest) &&
    graphPinterest.is_repin
  ) {
    return graphPinterest.is_repin
  }

  return false
}

export const PostEntity = {
  getId,
  isDraft,
  isSent,
  isPastReminder,
  isReminder,
  isPastDue,
  isPendingTiktok,
  isRetweet,
  isRepin,
  getScheduledAt,
  getDueAt,
  isScheduled,
  getFirstComment,
  needsApproval,
  userIsPostOwner,
  hasText,
  hasImage,
  hasVideo,
  hasSourceUrl,
  getChannelId,
  getChannelName,
  getChannel,
  validatePost,
  isPublishingPost,
  hasCampaign,
  getCampaign,
  isCustomScheduled,
  isInstagramReminder,
  getFormatedCreatedAt,
  getError,
  hasError,
  getErrorLink,
  getErrorLabel,
  isFetchedPostFromService,
  isShareAgainActionSupported,
  isPostChannelSupportedNewPostAnalytics,
  hasServiceLink,
  getPostAuthor,
  getLocation,
  getThreadsLocation,
}
